(() => {
  const MOBILE_QUERY =
    '(max-width: 749px)';

  const normalizeValue = (
    key,
    value
  ) => {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return '';
    }

    /*
      Handle serialized JSON metafield values.

      Example:
      ["UHS-I"]
      ["Waterproof","Shockproof"]
    */
    if (
      typeof value === 'string'
    ) {
      const trimmed =
        value.trim();

      if (
        (
          trimmed.startsWith('[') &&
          trimmed.endsWith(']')
        ) ||
        (
          trimmed.startsWith('{') &&
          trimmed.endsWith('}')
        )
      ) {
        try {
          const parsed =
            JSON.parse(
              trimmed
            );

          if (
            parsed !== value
          ) {
            return normalizeValue(
              key,
              parsed
            );
          }
        } catch (error) {
          /*
            Normal text value.
          */
        }
      }
    }

    /*
      Boolean specification labels.
    */
    if (
      key === 'write_protect' ||
      key === 'geo_tagging' ||
      key === 'wifi_capability'
    ) {
      if (
        value === true ||
        value === 1 ||
        value === '1' ||
        String(value)
          .toLowerCase() ===
          'true'
      ) {
        return 'Yes';
      }

      if (
        value === false ||
        value === 0 ||
        value === '0' ||
        String(value)
          .toLowerCase() ===
          'false'
      ) {
        return 'No';
      }
    }

    /*
      Shopify list metafields.
    */
    if (
      Array.isArray(value)
    ) {
      return value
        .map((item) =>
          normalizeValue(
            key,
            item
          )
        )
        .filter(Boolean)
        .join(', ');
    }

    /*
      Shopify metafield object.
    */
    if (
      typeof value ===
      'object'
    ) {
      if (
        value.value !==
        undefined
      ) {
        return normalizeValue(
          key,
          value.value
        );
      }

      return '';
    }

    return String(value);
  };


  /* ==========================================================
     CURRENT VARIANT
     ========================================================== */

  const getProductInfo = (
    root
  ) => {
    const productId =
      String(
        root.dataset
          .productId ||
        ''
      );

    const productInfos =
      Array.from(
        document
          .querySelectorAll(
            'product-info[data-product-id]'
          )
      );

    return (
      productInfos.find(
        (productInfo) =>
          String(
            productInfo
              .dataset
              .productId ||
            ''
          ) ===
          productId
      ) ||
      document.querySelector(
        'product-info'
      ) ||
      null
    );
  };


  const getCurrentVariantId = (
    root
  ) => {
    const productInfo =
      getProductInfo(
        root
      );

    const idInput =
      productInfo
        ?.querySelector(
          'form[action*="/cart/add"] input[name="id"]'
        ) ||
      productInfo
        ?.querySelector(
          'input[name="id"]'
        );

    return (
      idInput?.value ||
      root.dataset
        .initialVariantId ||
      null
    );
  };


  /* ==========================================================
     PRODUCT DETAILS
     ========================================================== */

  const initProductDetails = (
    root
  ) => {
    if (
      !root ||
      root.dataset
        .tsDetailsReady ===
        'true'
    ) {
      return;
    }


    /* ----------------------------------------------------------
       Tabs / accordion
       ---------------------------------------------------------- */

    const tabs =
      Array.from(
        root.querySelectorAll(
          '[data-ts-tab]'
        )
      );

    const accordions =
      Array.from(
        root.querySelectorAll(
          '[data-ts-accordion]'
        )
      );

    const panels =
      Array.from(
        root.querySelectorAll(
          '[data-ts-panel]'
        )
      );

    if (
      !tabs.length ||
      !panels.length
    ) {
      return;
    }

    const mobileMedia =
      window.matchMedia(
        MOBILE_QUERY
      );

    let lastActiveName =
      null;


    const getVisibleTabs =
      () =>
        tabs.filter(
          (tab) =>
            !tab.hidden
        );


    const getPanel = (
      name
    ) =>
      panels.find(
        (panel) =>
          panel.dataset
            .tsPanel ===
          name
      ) || null;


    const closeMobileAccordion =
      () => {
        accordions.forEach(
          (accordion) => {
            accordion
              .setAttribute(
                'aria-expanded',
                'false'
              );
          }
        );

        panels.forEach(
          (panel) => {
            panel.hidden =
              true;
          }
        );
      };


    const setActiveTab = (
      tabName,
      focus = false
    ) => {
      const visibleTabs =
        getVisibleTabs();

      const target =
        visibleTabs.find(
          (tab) =>
            tab.dataset
              .tsTab ===
            tabName
        ) ||
        visibleTabs[0];

      if (!target) {
        root.hidden =
          true;

        return;
      }

      const activeName =
        target.dataset
          .tsTab;

      lastActiveName =
        activeName;


      tabs.forEach(
        (tab) => {
          const active =
            tab === target;

          tab.setAttribute(
            'aria-selected',
            active
              ? 'true'
              : 'false'
          );

          tab.tabIndex =
            active
              ? 0
              : -1;
        }
      );


      accordions.forEach(
        (accordion) => {
          const active =
            accordion.dataset
              .tsAccordion ===
            activeName;

          accordion
            .setAttribute(
              'aria-expanded',
              active
                ? 'true'
                : 'false'
            );
        }
      );


      panels.forEach(
        (panel) => {
          panel.hidden =
            panel.dataset
              .tsPanel !==
            activeName;
        }
      );


      if (focus) {
        target.focus({
          preventScroll:
            true
        });
      }


      /*
        Desktop tab strip only.
      */
      if (
        !mobileMedia.matches
      ) {
        target
          .scrollIntoView({
            behavior:
              'smooth',
            block:
              'nearest',
            inline:
              'nearest'
          });
      }
    };


    /* ----------------------------------------------------------
       Desktop tabs
       ---------------------------------------------------------- */

    tabs.forEach(
      (tab) => {
        tab.addEventListener(
          'click',
          () => {
            setActiveTab(
              tab.dataset
                .tsTab
            );
          }
        );


        tab.addEventListener(
          'keydown',
          (event) => {
            if (
              ![
                'ArrowLeft',
                'ArrowRight',
                'Home',
                'End'
              ].includes(
                event.key
              )
            ) {
              return;
            }

            event
              .preventDefault();

            const visibleTabs =
              getVisibleTabs();

            const index =
              visibleTabs
                .indexOf(
                  tab
                );

            if (
              index === -1
            ) {
              return;
            }

            let nextIndex =
              index;

            if (
              event.key ===
              'ArrowRight'
            ) {
              nextIndex =
                (
                  index +
                  1
                ) %
                visibleTabs
                  .length;
            }

            else if (
              event.key ===
              'ArrowLeft'
            ) {
              nextIndex =
                (
                  index -
                  1 +
                  visibleTabs
                    .length
                ) %
                visibleTabs
                  .length;
            }

            else if (
              event.key ===
              'Home'
            ) {
              nextIndex =
                0;
            }

            else if (
              event.key ===
              'End'
            ) {
              nextIndex =
                visibleTabs
                  .length -
                1;
            }

            setActiveTab(
              visibleTabs[
                nextIndex
              ].dataset
                .tsTab,
              true
            );
          }
        );
      }
    );


    /* ----------------------------------------------------------
       Mobile accordion

       Tap closed section -> open.
       Tap same section -> close.
       ---------------------------------------------------------- */

    accordions.forEach(
      (accordion) => {
        accordion
          .addEventListener(
            'click',
            () => {
              if (
                accordion.hidden
              ) {
                return;
              }

              const name =
                accordion
                  .dataset
                  .tsAccordion;

              const panel =
                getPanel(
                  name
                );

              const isOpen =
                accordion
                  .getAttribute(
                    'aria-expanded'
                  ) ===
                  'true' &&
                panel &&
                !panel.hidden;

              if (
                isOpen
              ) {
                closeMobileAccordion();

                return;
              }

              setActiveTab(
                name
              );
            }
          );
      }
    );


    /* ==========================================================
       ABOUT — VARIANT IMAGE
       ========================================================== */

    const aboutImage =
      root.querySelector(
        '[data-ts-about-image]'
      );

    const aboutImagesElement =
      root.querySelector(
        '[data-ts-about-images]'
      );

    let aboutImages =
      {};


    if (
      aboutImagesElement
    ) {
      try {
        aboutImages =
          JSON.parse(
            aboutImagesElement
              .textContent ||
            '{}'
          );
      }

      catch (error) {
        console.warn(
          'TS Product Details: could not parse About image data.',
          error
        );
      }
    }


    const updateAboutImage = (
      variantId
    ) => {
      if (
        !aboutImage ||
        !variantId
      ) {
        return false;
      }

      const variantKey =
        String(
          variantId
        );

      if (
        !Object.prototype
          .hasOwnProperty.call(
            aboutImages,
            variantKey
          )
      ) {
        return false;
      }

      const imageUrl =
        aboutImages[
          variantKey
        ];

      if (!imageUrl) {
        return false;
      }

      /*
        Only update if the image
        actually changed.
      */
      const currentUrl =
        aboutImage
          .getAttribute(
            'src'
          );

      if (
        currentUrl !==
        imageUrl
      ) {
        aboutImage.src =
          imageUrl;

        /*
          If the initial image had
          responsive source information,
          remove it so the new src is
          definitely used.
        */
        aboutImage
          .removeAttribute(
            'srcset'
          );
      }

      return true;
    };


    /* ==========================================================
       SPECIFICATIONS
       ========================================================== */

    const specConfigElement =
      root.querySelector(
        '[data-ts-spec-config]'
      );

    const specTab =
      root.querySelector(
        '[data-ts-spec-tab]'
      );

    const specAccordion =
      root.querySelector(
        '[data-ts-spec-accordion]'
      );

    const specPanel =
      root.querySelector(
        '[data-ts-spec-panel]'
      );

    const specTable =
      root.querySelector(
        '[data-ts-spec-table]'
      );

    let specConfig =
      null;


    if (
      specConfigElement &&
      specTab &&
      specPanel &&
      specTable
    ) {
      try {
        specConfig =
          JSON.parse(
            specConfigElement
              .textContent ||
            '{}'
          );
      }

      catch (error) {
        console.warn(
          'TS Product Details: could not parse specifications JSON.',
          error
        );
      }
    }


    const renderSpecifications = (
      variantId
    ) => {
      if (
        !specConfig ||
        !specTab ||
        !specPanel ||
        !specTable
      ) {
        return false;
      }

      const variantKey =
        String(
          variantId
        );

      if (
        !specConfig
          .variants ||
        !Object.prototype
          .hasOwnProperty
          .call(
            specConfig
              .variants,
            variantKey
          )
      ) {
        /*
          Variant belongs to another
          product component.
        */
        return false;
      }


      const variant =
        specConfig
          .variants[
            variantKey
          ] ||
        {};

      const keys =
        specConfig.keys ||
        [];

      const labels =
        specConfig.labels ||
        [];

      const rows =
        [];


      keys.forEach(
        (
          key,
          index
        ) => {
          const displayValue =
            normalizeValue(
              key,
              variant[key]
            );

          if (
            !displayValue
          ) {
            return;
          }

          rows.push({
            label:
              labels[index] ||
              key,

            value:
              displayValue
          });
        }
      );


      const hasRows =
        rows.length >
        0;


      specTab.hidden =
        !hasRows;


      if (
        specAccordion
      ) {
        specAccordion.hidden =
          !hasRows;
      }


      if (
        !hasRows
      ) {
        specTable
          .replaceChildren();

        if (
          lastActiveName ===
          'specification'
        ) {
          const firstTab =
            getVisibleTabs()[0];

          if (
            firstTab
          ) {
            setActiveTab(
              firstTab
                .dataset
                .tsTab
            );
          }
        }

        return true;
      }


      const table =
        document.createElement(
          'table'
        );

      table.className =
        'ts-product-details__specs-table';


      const tbody =
        document.createElement(
          'tbody'
        );


      rows.forEach(
        (row) => {
          const tr =
            document.createElement(
              'tr'
            );

          const th =
            document.createElement(
              'th'
            );

          const td =
            document.createElement(
              'td'
            );

          th.scope =
            'row';

          th.textContent =
            row.label;

          td.textContent =
            row.value;

          tr.append(
            th,
            td
          );

          tbody
            .appendChild(
              tr
            );
        }
      );


      table.appendChild(
        tbody
      );


      specTable
        .replaceChildren(
          table
        );

      return true;
    };


    /* ==========================================================
       VARIANT UPDATE
       ========================================================== */

    const updateVariantContent = (
      variantId
    ) => {
      if (
        !variantId
      ) {
        return;
      }

      updateAboutImage(
        variantId
      );

      renderSpecifications(
        variantId
      );
    };


    root.dataset
      .tsDetailsReady =
      'true';


    const initialVariantId =
      getCurrentVariantId(
        root
      );


    if (
      initialVariantId
    ) {
      updateVariantContent(
        initialVariantId
      );
    }


    /*
      First available panel opens
      initially.
    */
    setActiveTab(
      getVisibleTabs()[0]
        ?.dataset
        .tsTab
    );


    /* ==========================================================
       VIEWPORT CHANGE
       ========================================================== */

    const handleViewportChange = (
      event
    ) => {
      if (
        !event.matches &&
        !panels.some(
          (panel) =>
            !panel.hidden
        )
      ) {
        setActiveTab(
          lastActiveName ||
          getVisibleTabs()[0]
            ?.dataset
            .tsTab
        );
      }
    };


    if (
      typeof mobileMedia
        .addEventListener ===
      'function'
    ) {
      mobileMedia
        .addEventListener(
          'change',
          handleViewportChange
        );
    }

    else {
      mobileMedia
        .addListener(
          handleViewportChange
        );
    }


    /* ==========================================================
       DAWN VARIANT CHANGE
       ========================================================== */

    if (
      typeof subscribe ===
        'function' &&
      typeof PUB_SUB_EVENTS !==
        'undefined' &&
      PUB_SUB_EVENTS
        .variantChange
    ) {
      subscribe(
        PUB_SUB_EVENTS
          .variantChange,
        (event) => {
          const variantId =
            event?.data
              ?.variant
              ?.id;

          if (
            !variantId
          ) {
            return;
          }

          /*
            Only update if the variant
            belongs to this product.
          */
          const variantKey =
            String(
              variantId
            );

          const belongsToAbout =
            Object.prototype
              .hasOwnProperty
              .call(
                aboutImages,
                variantKey
              );

          const belongsToSpecs =
            Boolean(
              specConfig
                ?.variants &&
              Object.prototype
                .hasOwnProperty
                .call(
                  specConfig
                    .variants,
                  variantKey
                )
            );

          if (
            !belongsToAbout &&
            !belongsToSpecs
          ) {
            return;
          }

          updateVariantContent(
            variantId
          );
        }
      );
    }


    /* ==========================================================
       VARIANT PICKER FALLBACK
       ========================================================== */

    document
      .addEventListener(
        'change',
        (event) => {
          const variantPicker =
            event.target
              .closest(
                'variant-selects'
              );

          if (
            !variantPicker
          ) {
            return;
          }

          const productInfo =
            variantPicker
              .closest(
                'product-info'
              );

          if (
            productInfo &&
            String(
              productInfo
                .dataset
                .productId ||
              ''
            ) !==
            String(
              root.dataset
                .productId ||
              ''
            )
          ) {
            return;
          }

          /*
            Give Dawn time to update
            the hidden variant ID.
          */
          window.setTimeout(
            () => {
              const variantId =
                getCurrentVariantId(
                  root
                );

              if (
                variantId
              ) {
                updateVariantContent(
                  variantId
                );
              }
            },
            100
          );
        }
      );
  };


  /* ==========================================================
     INITIALIZATION
     ========================================================== */

  const initAllProductDetails = (
    scope = document
  ) => {
    if (
      scope.matches?.(
        '[data-ts-product-details]'
      )
    ) {
      initProductDetails(
        scope
      );
    }

    scope
      .querySelectorAll?.(
        '[data-ts-product-details]'
      )
      .forEach(
        initProductDetails
      );
  };


  if (
    document.readyState ===
    'loading'
  ) {
    document
      .addEventListener(
        'DOMContentLoaded',
        () => {
          initAllProductDetails(
            document
          );
        },
        {
          once: true
        }
      );
  }

  else {
    initAllProductDetails(
      document
    );
  }


  /*
    Shopify Theme Editor.
  */
  document
    .addEventListener(
      'shopify:section:load',
      (event) => {
        initAllProductDetails(
          event.target
        );
      }
    );
})();